import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { API_CONFIG, AUTH_CONFIG } from '../shared/api/config'
import { getAccessToken, onAccessTokenChange } from '../shared/api/tokenStore'
import { HELD_MODULES, STANDARD_MODULES } from '../shared/constants/modules'
import { MODULE_REFUSED_EVENT } from '../shared/api/moduleRefusal'

/**
 * Which HELD modules (shared/constants/modules.js HELD_MODULES) the signed-in person's COMPANY is served -
 * and which STANDARD modules (STANDARD_MODULES) it has switched off. The two answers follow opposite
 * rules: a held module is released only by a literal `true`; a standard module is taken away only by a
 * literal `false`. Everything below about failing closed is about the HELD ones: a failed or missing
 * answer holds every held module and takes no standard module away.
 *
 * THE SERVER DECIDES, AND THIS ONLY LISTENS. Capital, Capex and Invoicing are refused by their own
 * services unless TWO keys are on (shared/module_switches.py, switch-on plan S1): the environment switch
 * on the backend task (CAPITAL_MODULE_ENABLED / CAPEX_MODULE_ENABLED / INVOICING_MODULE_ENABLED) AND the
 * company's own switch, which only a platform admin can turn on. Identity publishes the result for the
 * caller's company at GET /api/identity/modules/switches, and this provider reads it so the tile, the nav
 * tab and the address show a module exactly when the server will serve it.
 *
 * THE ANSWER IS PER COMPANY, SO IT IS ASKED WITH THE ACCESS TOKEN. The token lives in memory only
 * (shared/api/tokenStore.js) and on first paint there is none: it arrives when bootstrap() mints one
 * from the refresh cookie, or at sign-in. So this provider listens to the token store:
 *   - a token for a company it has not asked about -> ask (a rotated token for the SAME company does
 *     not ask again: the answer is the company's, and a module turned on shows on the next load);
 *   - no token and no remembered user -> nobody is signed in -> nothing is released;
 *   - no token YET but a remembered user -> a session is on its way -> `loading` stays true, so the route
 *     guard shows its loader instead of bouncing a reload of /capex to the portal. If no token arrives
 *     within TOKEN_WAIT_MS, it gives up and releases nothing.
 *
 * WHY NOT A LIST BAKED INTO THE PRODUCTION BUILD. promote.sh builds the frontend bundle with the same
 * VITE_* values for staging and production, and one bundle must show a module to the company that has
 * it and hide it from every other. Only the server knows which company is asking.
 *
 * FAILS CLOSED, in every direction:
 *   - while the answer is on its way, `loading` is true and every held module is held (the route guard
 *     shows its loader and decides nothing). That is EVERY question nobody has the answer to yet: the
 *     first, the one asked at sign-in, one for another company, and the one asked again after a
 *     failure — not only the one asked on first paint. Each of the later ones used to leave `loading`
 *     false while it was on its way, so a page that says "switched off" said it before the answer came;
 *   - a failed request, a non-2xx, a body that is not JSON (an older backend answering with the SPA's
 *     index.html) — all mean `{}`, so everything held stays held;
 *   - only a literal `true` releases a module; "1", "yes" and truthy objects do not;
 *   - signing out releases nothing.
 *
 * HELD IS NOT THE SAME AS "OFF". `answered` says whether `switches` is the server's answer for this
 * company (an ok response to the question asked with this company's token). Held for any other reason
 * — the lookup failed, nobody is signed in, the token never came — `answered` is false: every held
 * module is still held, but nothing may tell the person a module IS switched off, because nobody has
 * said so (a route's switched-off page draws only on an answer: components/ModuleSwitchRoute.jsx).
 *
 * A BARE `fetch`, NOT THE SHARED API CLIENT, on purpose. `createAPIClient` clears the session on a 401
 * when it cannot decode a live token; a lookup that only decides whether a tile is drawn must never be
 * able to sign anybody out. It attaches the token by hand and sends no cookies, and the server answers
 * it without ever using a 401.
 *
 * NOT CACHED between loads. A module turned off must not stay on screen from a stored answer.
 */

/** No provider (a test, a detached tree) is the same as "nothing is switched on": a definite answer. */
const NOTHING_SWITCHED_ON = Object.freeze({ switches: Object.freeze({}), loading: false, answered: true })

const ModuleSwitchContext = createContext(NOTHING_SWITCHED_ON)

/** How long to wait for the access token of a session that is expected (a remembered user) before giving up. */
export const TOKEN_WAIT_MS = 10000

/**
 * STAYING FRESH WHILE SIGNED IN. The answer used to be asked once per company and never again, so a
 * module a platform admin switched off stayed in a signed-in person's menu (its pages saying "Not
 * Found") and one switched on stayed hidden, until they signed in again. Now, while an answer is on
 * screen, the provider asks again - keeping that answer until the new one lands, so nothing flashes:
 *   - when the window regains focus or the tab becomes visible;
 *   - every RECHECK_EVERY_MS while the app is open;
 *   - when a service refuses a request the way a switched-off module does (shared/api/moduleRefusal.js).
 * Focus and refusal re-asks are at most one per RECHECK_MIN_GAP_MS. A failed re-ask changes nothing.
 * The server side takes effect within about 30 seconds of a change, so a person sees it within minutes.
 */
export const RECHECK_EVERY_MS = 3 * 60 * 1000
export const RECHECK_MIN_GAP_MS = 10 * 1000

/**
 * Keep only held module ids whose value is exactly `true`, and standard module ids whose value is
 * exactly `false` (switched off for this company - shared/constants/modules.js STANDARD_MODULES).
 * Anything else the server sends is ignored, so a held module stays held and a standard one stays on.
 */
export const readSwitches = (body) => {
  const out = {}
  if (!body || typeof body !== 'object' || Array.isArray(body)) return out
  for (const id of HELD_MODULES) {
    if (body[id] === true) out[id] = true
  }
  for (const id of STANDARD_MODULES) {
    if (body[id] === false) out[id] = false
  }
  return out
}

/**
 * The company a token belongs to, read from its payload WITHOUT verifying it — only to decide whether
 * to ask again. The server verifies; a token this cannot read simply means "ask".
 */
export const companyOf = (token) => {
  try {
    const part = String(token).split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='))
    const payload = JSON.parse(json)
    return payload.tenant_id || payload.company_id || null
  } catch {
    return null
  }
}

const rememberedUser = () => {
  try {
    return Boolean(localStorage.getItem(AUTH_CONFIG.USER_KEY))
  } catch {
    return false
  }
}

const sameSwitches = (a, b) => {
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  return ka.length === kb.length && ka.every((k) => a[k] === b[k])
}

export const MODULE_SWITCHES_URL = `${API_CONFIG.IDENTITY.baseURL}/modules/switches`

export function ModuleSwitchProvider({ children }) {
  const [state, setState] = useState(() => ({
    switches: {},
    // Nothing to wait for when nobody is signed in or about to be.
    loading: Boolean(getAccessToken()) || rememberedUser(),
    answered: false,
  }))
  // The company the current answer belongs to, the latest question asked, and whether what is on
  // screen is an answer at all. Refs, not state: the effect below must not re-run on its own result.
  const answeredFor = useRef(null)
  const latest = useRef(0)
  const holding = useRef(false)

  useEffect(() => {
    let alive = true
    let waiting = null

    const settle = (switches, answered) => {
      if (!alive) return
      holding.current = answered
      setState((prev) => (!prev.loading && prev.answered === answered && sameSwitches(prev.switches, switches)
        ? prev
        : { switches, loading: false, answered }))
    }

    // A QUESTION NOBODY HAS THE ANSWER TO YET: held, and loading, until it is answered — never the last
    // company's answer, and never a failure's `{}` read as "off".
    const begin = () => {
      if (!alive) return
      holding.current = false
      setState((prev) => (prev.loading && !prev.answered && Object.keys(prev.switches).length === 0
        ? prev
        : { switches: {}, loading: true, answered: false }))
    }

    const ask = async (token) => {
      const company = companyOf(token)
      if (company && company === answeredFor.current) return
      const mine = ++latest.current
      // A token whose company cannot be read asks every time, as it always has; while an answer is on
      // screen it stays there until the new one lands, so a module in use is not unmounted by an
      // ordinary token refresh. Every other question is a new one.
      if (company || !holding.current) begin()
      let switches = {}
      let ok = false
      try {
        const response = await fetch(MODULE_SWITCHES_URL, {
          cache: 'no-store',
          credentials: 'omit',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.ok) {
          switches = readSwitches(await response.json())
          ok = true
        }
      } catch {
        // Unreachable identity, a network blip, a body that is not JSON: every held module stays held.
        // Nothing to tell the person — a module they were never shown is not missing.
        switches = {}
      }
      if (mine !== latest.current) return          // a newer question has been asked since
      answeredFor.current = ok ? company : null    // a failure is not an answer: the next token asks again
      settle(switches, ok)
    }

    const signedOut = () => {
      latest.current += 1                          // any answer still on its way is for somebody else
      answeredFor.current = null
      settle({}, false)
    }

    const onToken = (token) => {
      if (token) {
        if (waiting) { clearTimeout(waiting); waiting = null }
        ask(token)
        return
      }
      // Cleared. Signing out clears the token and THEN forgets the user, in the same tick, so look
      // once the tick is over: no token and no remembered user is signed out.
      setTimeout(() => {
        if (alive && !getAccessToken() && !rememberedUser()) signedOut()
      }, 0)
    }

    // Re-ask for the company whose answer is on screen (see RECHECK_EVERY_MS). Never begins a new
    // question: `loading` stays false and the current answer stays until a newer one arrives.
    let lastRecheck = 0
    const recheck = async (force) => {
      const token = getAccessToken()
      if (!alive || !token || !holding.current) return
      const now = Date.now()
      if (!force && now - lastRecheck < RECHECK_MIN_GAP_MS) return
      lastRecheck = now
      const seq = latest.current
      const company = companyOf(token)
      try {
        const response = await fetch(MODULE_SWITCHES_URL, {
          cache: 'no-store',
          credentials: 'omit',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) return
        const switches = readSwitches(await response.json())
        // A newer question (a sign-in, another company) was asked meanwhile: its answer wins.
        if (!alive || seq !== latest.current || (company && company !== answeredFor.current)) return
        settle(switches, true)
      } catch {
        // Unreachable or not JSON: keep what is on screen.
      }
    }
    const onFocus = () => { recheck(false) }
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(false) }
    const onRefused = () => { recheck(false) }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener(MODULE_REFUSED_EVENT, onRefused)
    const every = setInterval(() => { recheck(true) }, RECHECK_EVERY_MS)

    const unsubscribe = onAccessTokenChange(onToken)
    const token = getAccessToken()
    if (token) {
      ask(token)
    } else if (rememberedUser()) {
      waiting = setTimeout(() => {
        waiting = null
        if (!getAccessToken()) signedOut()
      }, TOKEN_WAIT_MS)
    } else {
      settle({}, false)
    }

    return () => {
      alive = false
      if (waiting) clearTimeout(waiting)
      clearInterval(every)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(MODULE_REFUSED_EVENT, onRefused)
      unsubscribe()
    }
  }, [])

  return <ModuleSwitchContext.Provider value={state}>{children}</ModuleSwitchContext.Provider>
}

/**
 * `{ switches, loading, answered }` — `switches[id] === true` only for a held module the server serves this
 * company; `loading` while a question is on its way; `answered` only when `switches` IS the server's answer
 * for this company, so a held module may be called switched off.
 */
export const useModuleSwitches = () => useContext(ModuleSwitchContext) || NOTHING_SWITCHED_ON
